import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { ArrowRight, Button, TextInput } from '@astrid/components'
import ScoreIndicator from 'components/ScoreIndicator/ScoreIndicator'
import Modal from 'components/Modal/Modal'
import useThunkDispatch from 'hooks/useThunkDispatch'
import {
  changeStudySetName,
  createNewStudySet,
  deleteStudySet,
  deleteWordFromStudySet,
  selectCurrentStudySet
} from 'store/services/Exercise'
import { ReactComponent as DeleteIcon } from 'resources/graphics/icons/delete_word.svg'
import { ReactComponent as TrashIcon } from 'resources/graphics/icons/small_trash.svg'
import { routes } from 'routes/RouterStackComponent'

import { ConfirmDelete } from './ConfirmDelete'
import { SearchWords } from './SearchWords'
import styles from './StudySetMenuModal.module.scss'

interface StudySetMenuModalProps {
  onClose: () => void
}

const StudySetMenuModal = ({ onClose }: StudySetMenuModalProps) => {
  const studySet = useSelector(selectCurrentStudySet)
  const dispatch = useThunkDispatch()
  const history = useHistory()
  const [confirmDeleteNeeded, setConfirmDeleteNeeded] = useState(false)
  const [searchingWords, setSearchingWords] = useState(false)
  const [studySetName, setStudySetName] = useState(studySet?.name || '')

  const onDeleteWordClick = (word: string) => {
    if (studySet) {
      dispatch(deleteWordFromStudySet(studySet._id, word))
    }
  }

  const onDeleteStudySetClick = async () => {
    if (studySet) {
      await dispatch(deleteStudySet(studySet._id))
      onClose()
    }
  }

  const onSaveClick = async () => {
    if (studySet) {
      dispatch(changeStudySetName(studySet._id, studySetName))
    } else {
      dispatch(createNewStudySet(studySetName))
    }
  }

  const onStartPracticeClick = () => {
    history.push(`${routes.reading_level_up}/custom_set`)
  }

  const showSave = (studySet && studySet.name !== studySetName) || (!studySet && studySetName.length > 0)

  return (
    <Modal
      className={confirmDeleteNeeded || searchingWords ? styles.secondaryModal : styles.primaryModal}
      open
      closeModal={() => {
        if (confirmDeleteNeeded) {
          setConfirmDeleteNeeded(false)
        } else if (searchingWords) {
          setSearchingWords(false)
        } else {
          onClose()
        }
      }}
    >
      {confirmDeleteNeeded ? (
        <ConfirmDelete onCancel={() => setConfirmDeleteNeeded(false)} onDelete={onDeleteStudySetClick} />
      ) : searchingWords ? (
        <SearchWords />
      ) : (
        <>
          <div className={styles.content}>
            <div className={styles.titleContainer}>
              <span className={styles.title}>Group name</span>
              {showSave && (
                <button type="button" className={styles.saveNameButton} onClick={onSaveClick} aria-label={'Save title'}>
                  Save
                </button>
              )}
            </div>
            <TextInput
              id="name"
              name="name"
              label={'Type in name'}
              value={studySetName}
              onChange={(e) => setStudySetName(e.currentTarget.value)}
            />
            {studySet && (
              <div className={styles.studySetContainer}>
                <div className={styles.titleContainer}>
                  <span className={styles.title}>Word list</span>
                  {studySet.words.length > 0 && (
                    <span className={styles.mediumText}>
                      {studySet.words.length}
                      /10 words
                    </span>
                  )}
                </div>
                {studySet.words.map(({ word, score }, index) => (
                  <div key={`${word}${index}`} className={styles.wordContainer}>
                    <div className={styles.word}>{word}</div>
                    <div className={styles.wrapper}>
                      <ScoreIndicator
                        value={score}
                        sizeInPx={42}
                        strokeWidthInPx={7}
                        disableAnimation
                        fontSize={score === 100 ? '13px' : '16px'}
                        colorsThresholds={{
                          LOW: 50,
                          HIGH: 80
                        }}
                      />
                      <button
                        type="button"
                        aria-label={`Delete ${word}`}
                        className={styles.iconButton}
                        onClick={() => onDeleteWordClick(word)}
                      >
                        <DeleteIcon />
                      </button>
                    </div>
                  </div>
                ))}
                <Button type="button" color="confirm" onClick={() => setSearchingWords(true)}>
                  Add words
                </Button>
                <button
                  type="button"
                  aria-label={'Delete study set'}
                  className={styles.deleteButton}
                  onClick={() => setConfirmDeleteNeeded(true)}
                >
                  <TrashIcon className={styles.deleteIcon} />
                  <span className={styles.mediumText}>Delete the group</span>
                </button>
              </div>
            )}
          </div>
          {studySet && studySet.words.length > 0 && (
            <div className={styles.footer}>
              <Button variant="filled" color="light-blue" onClick={onStartPracticeClick}>
                Start practice
                <ArrowRight />
              </Button>
            </div>
          )}
        </>
      )}
    </Modal>
  )
}

export default StudySetMenuModal
